/**
 * Tag conditions in Drop Generator rules — "the hour / text / gateway / list
 * is (not) tagged X", X a Tag Rules tag of that kind (hours: judged on the
 * same campaign and clock; Phase 25: texts, gateways, lists too).
 * Three-valued like every condition: an item the tag cannot judge is unknown,
 * and negating an unknown stays unknown, so a missing tag never acts.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { HourTagVerdict } from "../../../tag-rules/hours/hour-tags.types";
import { hourTagWhy } from "../../../tag-rules/hours/hour-tags";
import { DROP_GEN_RULE_LIMITS, type DropGenRule, type DropGenRuleCheck, type DropGenRuleNode } from "../rules.types";

export type DropGenTagNode = Extract<DropGenRuleNode, { kind: "tag" }>;

/** One item's tags: the verdict of any tag name (unknown when absent). */
export interface DropGenTagContext {
  verdict(tagName: string): HourTagVerdict;
  /** What the item is, in a rule's words ("Text") — an hour when absent. */
  noun?: string;
}

/** Why an item's tag is unknown, in words — hours keep their own (visits, floors, tiers). */
export function tagUnknownWhy(v: HourTagVerdict, noun?: string): string {
  if (!noun || noun === "Hour") return hourTagWhy(v);
  const it = noun.toLowerCase();
  if (v.reason === "no_data") return `no tracker data for this ${it}`;
  if (v.reason === "no_rule") return `no enabled ${it} tag has this name`;
  if (v.reason === "unavailable") return `${it} tags could not be read`;
  return hourTagWhy(v);
}

const UNAVAILABLE: HourTagVerdict = { state: "unknown", uniqueVisits: 0, reason: "unavailable" };

export function evaluateTagNode(node: DropGenTagNode, tags?: DropGenTagContext): boolean | null {
  const v = tags?.verdict(node.tagName) ?? UNAVAILABLE;
  if (v.state === "unknown") return null;
  const tagged = v.state === "tagged";
  return node.negate ? !tagged : tagged;
}

/** "Hour tagged "Golden hour"" / "Text not tagged "Winner"" — with the reason when the tag cannot judge it. */
export function describeTagNode(node: DropGenTagNode, tags?: DropGenTagContext): string {
  const words = `${tags?.noun ?? "Hour"} ${node.negate ? "not tagged" : "tagged"} "${node.tagName}"`;
  const v = tags?.verdict(node.tagName) ?? UNAVAILABLE;
  return v.state === "unknown" ? `${words} (unknown: ${tagUnknownWhy(v, tags?.noun)})` : words;
}

export function tagNodeErrors(r: Record<string, unknown>, path: string, check: DropGenRuleCheck): string[] {
  if (!check.allowTags) return [`${path}: tag conditions are not allowed here`];
  const name = typeof r.tagName === "string" ? r.tagName.trim() : "";
  const errs: string[] = [];
  if (!name || name.length > DROP_GEN_RULE_LIMITS.tagName)
    errs.push(`${path}: a tag name of 1 to ${DROP_GEN_RULE_LIMITS.tagName} characters`);
  if (r.negate !== undefined && typeof r.negate !== "boolean") errs.push(`${path}: "is not tagged" is true or false`);
  return errs;
}

function namesIn(node: DropGenRuleNode, out: Set<string>): void {
  if (node.kind === "tag") out.add(node.tagName);
  else if (node.kind === "not") namesIn(node.child, out);
  else if (node.kind === "group") node.children.forEach((c) => namesIn(c, out));
}

/** Does the tree test a tag? */
export function usesTags(node: DropGenRuleNode): boolean {
  const out = new Set<string>();
  namesIn(node, out);
  return out.size > 0;
}

/** Every tag name the enabled rules test (none = no tags to read). */
export function ruleTagNames(rules: DropGenRule[] | undefined): string[] {
  const out = new Set<string>();
  for (const r of rules ?? []) if (r.enabled) namesIn(r.when, out);
  return [...out];
}

export const rulesNeedHourTags = (rules: DropGenRule[] | undefined): boolean => ruleTagNames(rules).length > 0;
/** Phase 25 — any surface: does an enabled rule test a tag (read the items' tags only then)? */
export const rulesNeedTags = rulesNeedHourTags;
